import { api } from '../../api/client';

export type AttributeType = 'text' | 'number' | 'enum' | 'bool';
export type Mount = 'smd' | 'tht' | 'other';
export type ValueKind =
  | 'resistance'
  | 'capacitance'
  | 'inductance'
  | 'voltage'
  | 'current'
  | 'power'
  | 'frequency';

export interface MeasurementProfile {
  mode: 'mass' | 'tape';
  sample_quantity: number;
  sample_weight_g: string;
  carrier_weight_g: string;
  tape_length_10_cells_mm: string | null;
}

export interface EnumOption {
  value: string;
  label_ru: string;
  label_en: string;
}

/** One field of the dynamic form. `is_identity` marks the fields that decide
 *  whether two entries are the same part — worth showing, because those are
 *  exactly the ones people skip without realising what it costs. */
export interface Attribute {
  key: string;
  label_ru: string;
  label_en: string;
  data_type: AttributeType;
  unit: string | null;
  enum_values: EnumOption[] | null;
  is_required: boolean;
  is_identity: boolean;
  sort_order: number;
}

export interface ComponentType {
  id: string;
  code: string;
  name_ru: string;
  name_en: string;
  parent_id: string | null;
  value_kind: ValueKind | null;
  sort_order: number;
}

export interface ComponentTypeDetail extends ComponentType {
  attributes: Attribute[];
}

export interface Package {
  id: string;
  code: string;
  name_ru: string | null;
  name_en: string | null;
  family: string | null;
  mount: Mount;
  size_mm: string | null;
  pin_count: number | null;
  aliases: string[] | null;
  /** Values determined by the package itself, keyed by attribute name. */
  default_attributes: Record<string, string>;
  measurement_profile: MeasurementProfile | null;
  description: string | null;
  datasheet_url: string | null;
  is_enabled: boolean;
  usage_count: number;
}

export interface Color {
  id: string;
  code: string;
  name_ru: string;
  name_en: string;
  hex: string | null;
  hex_secondary: string | null;
  is_enabled: boolean;
  usage_count: number;
  sort_order: number;
}

export interface Manufacturer {
  id: string;
  name: string;
  aliases: string[] | null;
  website: string | null;
}

export interface Unit {
  code: string;
  name_ru: string;
  name_en: string;
  decimals: number;
  sort_order: number;
}

export interface ComponentBrief {
  id: string;
  internal_code: string;
  name: string;
  mpn: string | null;
  value_display: string | null;
  qty_on_hand: string;
  /** Tracked unit by unit. The screens that only have a brief — an order line
   *  being received — are exactly the ones that must ask for serial numbers. */
  track_serials: boolean;
}

export interface Component extends ComponentBrief {
  reserved_qty: string;
  free_qty: string;
  manufacturer_id: string | null;
  component_type_id: string | null;
  category_id: string | null;
  description: string | null;
  value_base: string | null;
  value_unit: string | null;
  package_id: string | null;
  color_id: string | null;
  unit_code: string;
  package_qty: string | null;
  attributes: Record<string, unknown>;
  identity_key: string | null;
  datasheet_url: string | null;
  track_serials: boolean;
  min_stock: string | null;
  /** Explicit override; null inherits the package profile. */
  measurement_profile: MeasurementProfile | null;
  resolved_measurement_profile: MeasurementProfile | null;
  measurement_profile_source: 'component' | 'package' | null;
  /** Set when the part was taken out of the catalogue. */
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  manufacturer_name: string | null;
  type_name: string | null;
  package_code: string | null;
  package_description: string | null;
  package_datasheet_url: string | null;
  color_code: string | null;
}

export interface ComponentInput {
  name?: string | null;
  mpn?: string | null;
  manufacturer_id?: string | null;
  component_type_id?: string | null;
  category_id?: string | null;
  description?: string | null;
  value?: string | null;
  package_id?: string | null;
  color_id?: string | null;
  unit_code?: string;
  package_qty?: string | null;
  attributes?: Record<string, unknown>;
  datasheet_url?: string | null;
  track_serials?: boolean;
  min_stock?: string | null;
  measurement_profile?: MeasurementProfile | null;
}

export type DuplicateReason =
  | 'same_mpn'
  | 'similar_mpn'
  | 'same_identity'
  | 'same_parameters';

export interface DuplicateCandidate {
  component: ComponentBrief;
  reason: DuplicateReason;
  score: number;
}

export interface DuplicateReport {
  candidates: DuplicateCandidate[];
  suggested_name: string;
  value_display: string | null;
  value_base: string | null;
  value_unit: string | null;
  identity_key: string | null;
  signature: string | null;
}

export interface SearchHit {
  /** The whole row, not a brief: a table with a column per parameter of the
   *  chosen type has nowhere else to get the parameters from. */
  component: Component;
  score: number;
}

/** How results are ordered. Relevance stays the default: it is the only one
 *  that answers "where is the thing I just described". */
export type SearchSort = 'relevance' | 'name' | 'code' | 'created' | 'received';

export const SEARCH_SORTS: SearchSort[] = ['relevance', 'name', 'code', 'created', 'received'];

/** Hidden by default, mixed in on request, or on their own — the last is what
 *  makes the archive a place you can look through rather than sift for. */
export type ArchivedFilter = 'hide' | 'include' | 'only';

export const ARCHIVED_FILTERS: ArchivedFilter[] = ['hide', 'include', 'only'];

export interface SearchParams {
  q?: string;
  component_type_id?: string | null;
  package_id?: string | null;
  color_id?: string | null;
  manufacturer_id?: string | null;
  value_min?: string;
  value_max?: string;
  /** Parameter filters, as key -> value. Sent as repeated `attr=key:value`. */
  attributes?: Record<string, string>;
  in_stock?: boolean;
  /** Parts taken out of the catalogue: hidden, mixed in, or on their own. */
  archived?: ArchivedFilter;
  sort?: SearchSort;
  /** The server's own default is 50 and its ceiling 200. A card-at-a-time list
   *  wants the default; a table wants as much of the shelf as it can get. */
  limit?: number;
}

/** `all` — the whole tree, for the entry form; `used` — only types that occur
 *  in stored components, which is the only sensible set for a search filter. */
export const listTypes = (scope: 'all' | 'used' = 'all') =>
  api.get<ComponentType[]>(`/reference/types?scope=${scope}`);

export const getType = (id: string) => api.get<ComponentTypeDetail>(`/reference/types/${id}`);

/** `enabled` — the short list, for entry; `all` — the full shipped catalogue;
 *  `used` — only packages that actually occur in components, which is the only
 *  sensible set for a search filter: offering one nothing is stored in offers a
 *  filter that returns nothing. */
export const listPackages = (scope: 'enabled' | 'all' | 'used', q?: string) => {
  const params = new URLSearchParams({ scope });
  if (q) params.set('q', q);
  return api.get<Package[]>(`/reference/packages?${params}`);
};

export const listColors = (scope: 'enabled' | 'all') =>
  api.get<Color[]>(`/reference/colors?scope=${scope}`);

export const listManufacturers = (q?: string) =>
  api.get<Manufacturer[]>(`/reference/manufacturers${q ? `?q=${encodeURIComponent(q)}` : ''}`);

export const listUnits = () => api.get<Unit[]>('/reference/units');

export const enablePackage = (id: string) =>
  api.post<Package>(`/reference/packages/${id}/enable`);

export const enableColor = (id: string) => api.post<Color>(`/reference/colors/${id}/enable`);

export const searchComponents = (params: SearchParams) => {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.component_type_id) query.set('component_type_id', params.component_type_id);
  if (params.package_id) query.set('package_id', params.package_id);
  if (params.color_id) query.set('color_id', params.color_id);
  if (params.manufacturer_id) query.set('manufacturer_id', params.manufacturer_id);
  if (params.value_min) query.set('value_min', params.value_min);
  if (params.value_max) query.set('value_max', params.value_max);
  if (params.in_stock) query.set('in_stock', 'true');
  if (params.archived && params.archived !== 'hide') query.set('archived', params.archived);
  if (params.sort && params.sort !== 'relevance') query.set('sort', params.sort);
  if (params.limit) query.set('limit', String(params.limit));
  for (const [key, value] of Object.entries(params.attributes ?? {})) {
    if (value) query.append('attr', `${key}:${value}`);
  }
  return api.get<SearchHit[]>(`/components/search?${query}`);
};

/** The same rows as a file, and all of them: on screen two hundred is as much as
 *  is worth drawing, in a file the point is to have the lot. A column per
 *  parameter of the chosen type, the same as on screen. */
export const searchXlsx = (params: SearchParams) => {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.component_type_id) query.set('component_type_id', params.component_type_id);
  if (params.package_id) query.set('package_id', params.package_id);
  if (params.manufacturer_id) query.set('manufacturer_id', params.manufacturer_id);
  if (params.value_min) query.set('value_min', params.value_min);
  if (params.value_max) query.set('value_max', params.value_max);
  if (params.in_stock) query.set('in_stock', 'true');
  for (const [key, value] of Object.entries(params.attributes ?? {})) {
    if (value) query.append('attr', `${key}:${value}`);
  }
  return api.blob(`/components/search.xlsx?${query}`);
};

export const getComponent = (id: string) => api.get<Component>(`/components/${id}`);

export interface BomUsageProject {
  project_id: string;
  code: string;
  name: string;
  status: 'planned' | 'active';
  units: number;
  pinned_version_id: string;
  pinned_version_code: string;
  pinned_version: number;
}

export interface BomUsage {
  specification_id: string;
  specification_name: string;
  version_id: string;
  version_code: string;
  version: number;
  status: 'draft' | 'released' | 'deprecated';
  component_id: string;
  /** Alternatives are shown for context; only primary lines create demand. */
  kind: 'primary' | 'alternative';
  direct: boolean;
  reference_designators: string | null;
  quantity_per_product: string;
  calculation: string;
  is_optional: boolean;
  path: string[];
  projects: BomUsageProject[];
}

export const componentBomUsage = (
  componentId: string,
  filters: { statuses?: string[]; activeProjectsOnly?: boolean } = {},
) => {
  const query = new URLSearchParams();
  for (const status of filters.statuses ?? []) query.append('status', status);
  if (filters.activeProjectsOnly) query.set('active_projects_only', 'true');
  return api.get<BomUsage[]>(`/components/${componentId}/bom-usage?${query}`);
};

/** Bring a part back into the catalogue. Archiving destroyed nothing, so an
 *  archiving done by mistake should not be permanent. */
export const restoreComponent = (id: string) =>
  api.post<Component>(`/components/${id}/restore`);

export const createComponent = (input: ComponentInput) =>
  api.post<Component>('/components', input);

export const updateComponent = (id: string, input: ComponentInput) =>
  api.patch<Component>(`/components/${id}`, input);

export const archiveComponent = (id: string) => api.delete<void>(`/components/${id}`);

/** Called as the form is filled in, not on submit. A warning that arrives after
 *  the work is done is a warning that gets dismissed. */
export const checkDuplicates = (input: ComponentInput & { exclude_id?: string | null }) =>
  api.post<DuplicateReport>('/components/check-duplicates', input);

export interface Cluster {
  identity_key: string;
  components: ComponentBrief[];
}

export interface MergeReport {
  source_id: string;
  target_id: string;
  /** What is printed on labels already stuck to drawers. They keep working. */
  source_code: string;
  moved: Record<string, number>;
  dropped: Record<string, number>;
}

export interface Dismissal {
  id: string;
  identity_key: string;
  component_ids: string[];
  note: string | null;
  created_at: string;
}

export const listDuplicateClusters = () =>
  api.get<Cluster[]>('/components/duplicate-clusters');

/** "These are different parts." Recorded against this exact set of entries, so
 *  it holds until a new entry joins the cluster — the one nobody has judged. */
export const dismissCluster = (identityKey: string, componentIds: string[]) =>
  api.post<Dismissal>('/components/duplicate-clusters/dismiss', {
    identity_key: identityKey,
    component_ids: componentIds,
  });

export const listDismissals = () =>
  api.get<Dismissal[]>('/components/duplicate-clusters/dismissed');

export const undismissCluster = (id: string) =>
  api.delete<void>(`/components/duplicate-clusters/dismissed/${id}`);

export const mergeInto = (sourceId: string, targetId: string) =>
  api.post<MergeReport>(`/components/${sourceId}/merge-into/${targetId}`);

export interface ComponentImage {
  id: string;
  file_id: string;
  caption: string | null;
  is_primary: boolean;
  sort_order: number;
  file: {
    id: string;
    original_filename: string;
    mime: string;
    size_bytes: number;
    width: number | null;
    height: number | null;
  };
}

export interface ManufacturerInput {
  name: string;
  aliases?: string[] | null;
  website?: string | null;
}

export interface PackageInput {
  code: string;
  name_ru?: string | null;
  mount: Mount;
  size_mm?: string | null;
  pin_count?: number | null;
  description?: string | null;
  datasheet_url?: string | null;
}

export type PackageUpdate = Omit<PackageInput, 'code'>;

/** A subtype under an existing type: "Печатная плата" -> "Zigbee". */
export interface ComponentTypeInput {
  code: string;
  name_ru: string;
  parent_id?: string | null;
}

export const createManufacturer = (input: ManufacturerInput) =>
  api.post<Manufacturer>('/reference/manufacturers', input);

export const createPackage = (input: PackageInput) =>
  api.post<Package>('/reference/packages', input);

export const updatePackage = (id: string, input: PackageUpdate) =>
  api.patch<Package>(`/reference/packages/${id}`, input);

export const updatePackageMeasurementProfile = (
  id: string,
  measurement_profile: MeasurementProfile | null,
) => api.patch<Package>(`/reference/packages/${id}/measurement-profile`, { measurement_profile });

export const listPackageImages = (packageId: string) =>
  api.get<ComponentImage[]>(`/reference/packages/${packageId}/images`);

export const attachPackageImage = (packageId: string, fileId: string) =>
  api.post<ComponentImage>(`/reference/packages/${packageId}/images?file_id=${fileId}`);

export const detachPackageImage = (packageId: string, imageId: string) =>
  api.delete<void>(`/reference/packages/${packageId}/images/${imageId}`);

export const createType = (input: ComponentTypeInput) =>
  api.post<ComponentTypeDetail>('/reference/types', input);

export const listComponentImages = (componentId: string) =>
  api.get<ComponentImage[]>(`/components/${componentId}/images`);

export const attachComponentImage = (componentId: string, fileId: string) =>
  api.post<ComponentImage>(`/components/${componentId}/images?file_id=${fileId}`);

export const setPrimaryComponentImage = (componentId: string, imageId: string) =>
  api.post<ComponentImage>(`/components/${componentId}/images/${imageId}/primary`);

export const detachComponentImage = (componentId: string, imageId: string) =>
  api.delete<void>(`/components/${componentId}/images/${imageId}`);

export type SubstituteKind = 'drop_in' | 'conditional';

export interface Substitute {
  id: string;
  kind: SubstituteKind;
  note: string | null;
  component: ComponentBrief;
}

export const listSubstitutes = (componentId: string) =>
  api.get<Substitute[]>(`/components/${componentId}/substitutes`);

export const addSubstitute = (
  componentId: string,
  body: { substitute_id: string; kind: SubstituteKind; note: string | null; mutual: boolean },
) => api.post<Substitute>(`/components/${componentId}/substitutes`, body);

export const updateSubstitute = (
  componentId: string,
  linkId: string,
  body: { kind?: SubstituteKind; note?: string | null },
) => api.patch<Substitute>(`/components/${componentId}/substitutes/${linkId}`, body);

export const removeSubstitute = (componentId: string, linkId: string) =>
  api.delete<void>(`/components/${componentId}/substitutes/${linkId}`);
